import React, { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../../utils/apiBase';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Download,
  Eye,
  EyeOff,
  LifeBuoy,
  Loader2,
  PlugZap,
  RefreshCw,
  Save,
  XCircle,
} from 'lucide-react';

interface Props {
  session: any;
}

interface CampoEtapa {
  id: string;
  nome: string;
  tipo: string;
  opcoes?: string[];
}

interface EtapaFluxo {
  id: string;
  nome: string;
  campos: CampoEtapa[];
}

interface FluxoResumo {
  id: string;
  nome: string;
  etapas: EtapaFluxo[];
}

interface Mapeamento {
  requerente: string;
  titulo: string;
  descricao: string;
  categoria: string;
}

interface GlpiConfig {
  enabled: boolean;
  url: string;
  appToken: string;
  userToken: string;
  usuario: string;
  senha: string;
  authMode: 'auto' | 'user_token' | 'basic';
  fluxoId: string;
  etapaId: string;
  mapeamento: Mapeamento;
  statusFiltro: number[];
  criarOpcoesAuto: boolean;
  autoSync: boolean;
  intervaloMin: number;
  limitePorSync: number;
}

interface TicketGlpi {
  id: number;
  titulo: string;
  descricao: string;
  categoria: string;
  requerente: string;
  status: number;
  statusNome: string;
  dataAbertura: string;
  entidade?: string;
  jaImportado?: boolean;
  cardId?: string;
}

const DEFAULTS: GlpiConfig = {
  enabled: false,
  url: '',
  appToken: '',
  userToken: '',
  usuario: '',
  senha: '',
  authMode: 'auto',
  fluxoId: '',
  etapaId: '',
  mapeamento: { requerente: '', titulo: '', descricao: '', categoria: '' },
  statusFiltro: [1, 2, 3, 4],
  criarOpcoesAuto: true,
  autoSync: false,
  intervaloMin: 15,
  limitePorSync: 30,
};

const STATUS_GLPI: Array<{ id: number; nome: string }> = [
  { id: 1, nome: 'Novo' },
  { id: 2, nome: 'Em atendimento (atribuído)' },
  { id: 3, nome: 'Em atendimento (planejado)' },
  { id: 4, nome: 'Pendente' },
  { id: 5, nome: 'Solucionado' },
  { id: 6, nome: 'Fechado' },
];

// Rótulos e dicas de cada campo do GLPI que vira campo de card
const CAMPOS_GLPI: Array<{ chave: keyof Mapeamento; label: string; dica: string; tiposSugeridos: string[] }> = [
  { chave: 'requerente', label: 'Requerente (Nome)', dica: 'Nome de quem abriu o chamado no GLPI', tiposSugeridos: ['texto'] },
  { chave: 'titulo', label: 'Título', dica: 'Já vira o título do card; escolha um campo se também quiser guardá-lo', tiposSugeridos: ['texto'] },
  { chave: 'descricao', label: 'Descrição', dica: 'Conteúdo do chamado (HTML é convertido em texto)', tiposSugeridos: ['texto_longo', 'texto'] },
  { chave: 'categoria', label: 'Categoria', dica: 'Categoria ITIL do chamado', tiposSugeridos: ['lista', 'texto'] },
];

const Toggle: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, description, checked, disabled, onChange }) => (
  <div className={`flex items-start justify-between gap-4 py-3 ${disabled ? 'opacity-50' : ''}`}>
    <div className="flex-1">
      <div className="text-sm font-medium text-gray-800">{label}</div>
      {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 ${
        checked ? 'bg-indigo-600' : 'bg-gray-300'
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  </div>
);

const IntegracaoGlpi: React.FC<Props> = ({ session }) => {
  const empresaId: string | undefined = session?.empresa?.id || session?.usuario?.empresaId;
  const usuarioNome: string | undefined = session?.usuario?.nome || session?.usuario?.email;

  const [config, setConfig] = useState<GlpiConfig>(DEFAULTS);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const [fluxos, setFluxos] = useState<FluxoResumo[]>([]);
  const [mostrarSenhas, setMostrarSenhas] = useState(false);
  const [mostrarAvancado, setMostrarAvancado] = useState(false);

  const [testando, setTestando] = useState(false);
  const [testeResultado, setTesteResultado] = useState<{ ok: boolean; mensagem: string; detalhes?: any } | null>(null);

  const [tickets, setTickets] = useState<TicketGlpi[]>([]);
  const [buscandoTickets, setBuscandoTickets] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [importando, setImportando] = useState(false);
  const [importResultado, setImportResultado] = useState<any>(null);

  const headers = () => ({
    'Content-Type': 'application/json',
    'X-User-Data': JSON.stringify({
      email: session?.usuario?.email,
      nome: session?.usuario?.nome,
      role: session?.usuario?.role,
      empresaId,
    }),
  });

  // ─── Carregamento inicial ────────────────────────────────────
  useEffect(() => {
    if (!empresaId) {
      setCarregando(false);
      setErro('empresaId não encontrado na sessão. Faça login novamente.');
      return;
    }
    setCarregando(true);
    Promise.all([
      fetch(apiUrl(`/glpi/config?empresaId=${encodeURIComponent(empresaId)}`), { headers: headers() })
        .then(r => r.json())
        .then(j => {
          if (j?.status === 'success' && j?.config) {
            setConfig({ ...DEFAULTS, ...j.config, mapeamento: { ...DEFAULTS.mapeamento, ...(j.config.mapeamento || {}) } });
          }
        }),
      fetch(apiUrl(`/glpi/fluxos?empresaId=${encodeURIComponent(empresaId)}`), { headers: headers() })
        .then(r => r.json())
        .then(j => setFluxos(j?.fluxos || [])),
    ])
      .catch(e => setErro(`Falha ao carregar: ${e?.message || e}`))
      .finally(() => setCarregando(false));
  }, [empresaId]);

  const set = <K extends keyof GlpiConfig>(k: K, v: GlpiConfig[K]) => setConfig(c => ({ ...c, [k]: v }));
  const setMap = (k: keyof Mapeamento, v: string) =>
    setConfig(c => ({ ...c, mapeamento: { ...c.mapeamento, [k]: v } }));

  const fluxoSelecionado = useMemo(
    () => fluxos.find(f => f.id === config.fluxoId) || null,
    [fluxos, config.fluxoId],
  );
  const etapaSelecionada = useMemo(
    () => fluxoSelecionado?.etapas.find(e => e.id === config.etapaId) || null,
    [fluxoSelecionado, config.etapaId],
  );

  const camposDisponiveis = etapaSelecionada?.campos || [];

  // ─── Ações ───────────────────────────────────────────────────
  const salvar = async () => {
    if (!empresaId) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await fetch(apiUrl('/glpi/config'), {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ empresaId, glpi: config, usuario: usuarioNome }),
      });
      const j = await r.json();
      if (!r.ok || j?.status !== 'success') throw new Error(j?.message || `HTTP ${r.status}`);
      setConfig({ ...DEFAULTS, ...j.config, mapeamento: { ...DEFAULTS.mapeamento, ...(j.config.mapeamento || {}) } });
      setSalvoEm(new Date());
    } catch (e: any) {
      setErro(`Falha ao salvar: ${e?.message || e}`);
    } finally {
      setSalvando(false);
    }
  };

  const testarConexao = async () => {
    setTestando(true);
    setTesteResultado(null);
    try {
      const r = await fetch(apiUrl('/glpi/testar'), {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ empresaId, glpi: config }),
      });
      const j = await r.json();
      if (j?.status === 'success') {
        setTesteResultado({ ok: true, mensagem: j.message || 'Conexão OK', detalhes: j.detalhes });
      } else {
        setTesteResultado({ ok: false, mensagem: j?.message || `HTTP ${r.status}` });
      }
    } catch (e: any) {
      setTesteResultado({ ok: false, mensagem: e?.message || 'Erro de rede' });
    } finally {
      setTestando(false);
    }
  };

  const buscarTickets = async () => {
    if (!empresaId) return;
    setBuscandoTickets(true);
    setImportResultado(null);
    try {
      const qs = new URLSearchParams({ empresaId, limite: String(config.limitePorSync || 30) });
      if (config.statusFiltro?.length) qs.set('status', config.statusFiltro.join(','));
      const r = await fetch(apiUrl(`/glpi/tickets?${qs.toString()}`), { headers: headers() });
      const j = await r.json();
      if (j?.status === 'success') {
        setTickets(j.tickets || []);
        setSelecionados(new Set((j.tickets || []).filter((t: TicketGlpi) => !t.jaImportado).map((t: TicketGlpi) => t.id)));
        setErro(null);
      } else {
        setTickets([]);
        setErro(j?.message || 'Falha ao buscar tickets');
      }
    } catch (e: any) {
      setErro(`Falha ao buscar tickets: ${e?.message || e}`);
    } finally {
      setBuscandoTickets(false);
    }
  };

  const importar = async () => {
    if (!empresaId || selecionados.size === 0) return;
    setImportando(true);
    setImportResultado(null);
    try {
      const r = await fetch(apiUrl('/glpi/importar'), {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          empresaId,
          fluxoId: config.fluxoId,
          etapaId: config.etapaId,
          ticketIds: Array.from(selecionados),
        }),
      });
      const j = await r.json();
      setImportResultado(j);
      if (j?.status === 'success') {
        const criados = new Set<number>((j.cards || []).map((c: any) => Number(c.ticket)));
        setTickets(prev => prev.map(t => (criados.has(t.id) ? { ...t, jaImportado: true } : t)));
        setSelecionados(new Set());
      }
    } catch (e: any) {
      setImportResultado({ status: 'error', message: e?.message || 'Erro de rede' });
    } finally {
      setImportando(false);
    }
  };

  const toggleSelecionado = (id: number) => {
    setSelecionados(prev => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const toggleStatus = (id: number) => {
    setConfig(c => {
      const atual = new Set(c.statusFiltro || []);
      if (atual.has(id)) atual.delete(id);
      else atual.add(id);
      return { ...c, statusFiltro: Array.from(atual).sort((a, b) => a - b) };
    });
  };

  const mapeamentoIncompleto = !config.mapeamento.requerente || !config.mapeamento.descricao || !config.mapeamento.categoria;
  const destinoOk = Boolean(config.fluxoId && config.etapaId);

  if (carregando) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm py-8 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando configuração...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-sky-50 to-indigo-50 border border-sky-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="bg-sky-600 text-white rounded-lg w-10 h-10 flex items-center justify-center flex-shrink-0">
            <LifeBuoy className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-sky-900 flex items-center gap-2">
              GLPI (Helpdesk)
              {config.enabled ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-600 text-white uppercase tracking-wide">Ativa</span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-400 text-white uppercase tracking-wide">Desativada</span>
              )}
            </h3>
            <p className="text-xs text-sky-900/80 mt-0.5">
              Busca os chamados do GLPI pela API REST e cria cards no fluxo escolhido, preenchendo
              <strong> Requerente</strong>, <strong>Título</strong>, <strong>Descrição</strong> e <strong>Categoria</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* 1. Conexão */}
      <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
        <h4 className="text-sm font-semibold text-gray-800">1. Conexão com o GLPI</h4>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            URL da API REST <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={config.url}
            onChange={e => set('url', e.target.value)}
            placeholder="http://servidor/glpi/apirest.php"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Endereço do GLPI + <code className="bg-gray-100 px-1 rounded">/apirest.php</code>. Se você informar só o
            endereço, o <code className="bg-gray-100 px-1 rounded">/apirest.php</code> é acrescentado automaticamente.
            Lembre de habilitar a API em <em>Configurar → Geral → API</em>.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Token de API do usuário</label>
            <input
              type={mostrarSenhas ? 'text' : 'password'}
              value={config.userToken}
              onChange={e => set('userToken', e.target.value)}
              placeholder="Preferências → Token de API"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              App-Token <span className="text-gray-400">(opcional)</span>
            </label>
            <input
              type={mostrarSenhas ? 'text' : 'password'}
              value={config.appToken}
              onChange={e => set('appToken', e.target.value)}
              placeholder="Configurar → Geral → API → Clientes da API"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Usuário</label>
            <input
              type="text"
              value={config.usuario}
              onChange={e => set('usuario', e.target.value)}
              placeholder="escalone"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Senha</label>
            <input
              type={mostrarSenhas ? 'text' : 'password'}
              value={config.senha}
              onChange={e => set('senha', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
            />
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setMostrarSenhas(v => !v)}
            className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900"
          >
            {mostrarSenhas ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {mostrarSenhas ? 'Ocultar' : 'Mostrar'} tokens e senha
          </button>

          <button
            type="button"
            onClick={testarConexao}
            disabled={testando || !config.url}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg"
          >
            {testando ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />}
            {testando ? 'Testando...' : 'Testar conexão'}
          </button>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setMostrarAvancado(v => !v)}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mostrarAvancado ? 'rotate-180' : ''}`} />
            Autenticação avançada
          </button>
          {mostrarAvancado && (
            <div className="mt-2 flex items-center gap-2">
              <label className="text-xs text-gray-600">Modo:</label>
              <select
                value={config.authMode}
                onChange={e => set('authMode', e.target.value as GlpiConfig['authMode'])}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
              >
                <option value="auto">Automático (testa token e depois usuário/senha)</option>
                <option value="user_token">Somente Token de API</option>
                <option value="basic">Somente usuário e senha</option>
              </select>
            </div>
          )}
        </div>

        {testeResultado && (
          <div
            className={`rounded-lg border p-3 text-sm ${
              testeResultado.ok
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <div className="flex items-start gap-2">
              {testeResultado.ok ? (
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className="font-medium">{testeResultado.mensagem}</div>
                {testeResultado.detalhes && (
                  <div className="text-xs mt-1 space-y-0.5">
                    <div>URL: <code className="bg-white/60 px-1 rounded">{testeResultado.detalhes.url}</code></div>
                    <div>
                      Autenticação:{' '}
                      <strong>{testeResultado.detalhes.authMode === 'basic' ? 'usuário e senha' : 'token de API'}</strong>
                      {testeResultado.detalhes.usouAppToken ? ' + App-Token' : ''}
                    </div>
                    {testeResultado.detalhes.versaoGlpi && <div>Versão do GLPI: {testeResultado.detalhes.versaoGlpi}</div>}
                    <div>Chamados visíveis na amostra: {testeResultado.detalhes.amostraTickets?.length ?? 0}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 2. Destino + mapeamento */}
      <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
        <h4 className="text-sm font-semibold text-gray-800">2. Destino no Escalone</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fluxo</label>
            <select
              value={config.fluxoId}
              onChange={e => setConfig(c => ({ ...c, fluxoId: e.target.value, etapaId: '', mapeamento: { ...DEFAULTS.mapeamento } }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            >
              <option value="">Selecione o fluxo...</option>
              {fluxos.map(f => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Etapa (coluna onde o card entra)</label>
            <select
              value={config.etapaId}
              onChange={e => setConfig(c => ({ ...c, etapaId: e.target.value, mapeamento: { ...DEFAULTS.mapeamento } }))}
              disabled={!fluxoSelecionado}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            >
              <option value="">{fluxoSelecionado ? 'Selecione a etapa...' : 'Escolha o fluxo primeiro'}</option>
              {(fluxoSelecionado?.etapas || []).map(e => (
                <option key={e.id} value={e.id}>{e.nome}</option>
              ))}
            </select>
          </div>
        </div>

        {etapaSelecionada && (
          <div className="pt-1">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              Mapeamento dos campos — GLPI → campos da etapa “{etapaSelecionada.nome}”
            </div>
            <div className="space-y-2">
              {CAMPOS_GLPI.map(item => (
                <div key={item.chave} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-2 sm:items-center">
                  <div>
                    <div className="text-sm text-gray-800">{item.label}</div>
                    <div className="text-[11px] text-gray-500">{item.dica}</div>
                  </div>
                  <select
                    value={config.mapeamento[item.chave]}
                    onChange={e => setMap(item.chave, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  >
                    <option value="">{item.chave === 'titulo' ? '— não guardar em campo —' : '— não preencher —'}</option>
                    {camposDisponiveis.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.nome} ({c.tipo})
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {camposDisponiveis.length === 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                Esta etapa não tem campos configurados. Crie os campos no fluxo antes de mapear.
              </div>
            )}
            {mapeamentoIncompleto && camposDisponiveis.length > 0 && (
              <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Requerente, Descrição e Categoria ainda não estão mapeados. O que ficar sem mapeamento não é
                  preenchido no card.
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3. Filtros e comportamento */}
      <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
        <h4 className="text-sm font-semibold text-gray-800">3. Filtros e comportamento</h4>

        <div>
          <div className="text-xs font-medium text-gray-600 mb-1.5">Importar chamados com status:</div>
          <div className="flex flex-wrap gap-2">
            {STATUS_GLPI.map(s => {
              const ativo = (config.statusFiltro || []).includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleStatus(s.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    ativo
                      ? 'bg-sky-600 border-sky-600 text-white'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  {s.nome}
                </button>
              );
            })}
          </div>
          {(config.statusFiltro || []).length === 0 && (
            <p className="text-[11px] text-gray-500 mt-1">Nenhum status marcado = traz todos.</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Máximo de chamados por busca</label>
            <input
              type="number"
              min={1}
              max={200}
              value={config.limitePorSync}
              onChange={e => set('limitePorSync', Number(e.target.value) || 30)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Intervalo do sync automático (min)</label>
            <input
              type="number"
              min={5}
              max={1440}
              value={config.intervaloMin}
              onChange={e => set('intervaloMin', Number(e.target.value) || 15)}
              disabled={!config.autoSync}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:bg-gray-50"
            />
          </div>
        </div>

        <hr className="border-gray-100" />

        <Toggle
          label="Integração GLPI habilitada"
          description="Desligada, a integração não busca nem importa nada."
          checked={config.enabled}
          onChange={v => set('enabled', v)}
        />
        <Toggle
          label="Criar opções de lista automaticamente"
          description="Se a categoria do GLPI não existir nas opções do campo de lista, ela é acrescentada ao campo."
          checked={config.criarOpcoesAuto}
          disabled={!config.enabled}
          onChange={v => set('criarOpcoesAuto', v)}
        />
        <Toggle
          label="Sincronizar automaticamente"
          description="Importa chamados novos no intervalo configurado, sem precisar clicar em Importar. Chamados já importados nunca duplicam."
          checked={config.autoSync}
          disabled={!config.enabled || !destinoOk}
          onChange={v => set('autoSync', v)}
        />
      </div>

      {/* Salvar */}
      <div className="flex items-center justify-between">
        <div className="text-xs">
          {salvoEm && <span className="text-emerald-700">✓ Salvo às {salvoEm.toLocaleTimeString()}</span>}
          {erro && <span className="text-red-600">{erro}</span>}
        </div>
        <button
          type="button"
          onClick={salvar}
          disabled={salvando || !empresaId}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg shadow-sm"
        >
          {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {salvando ? 'Salvando...' : 'Salvar configuração'}
        </button>
      </div>

      {/* 4. Buscar e importar */}
      <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h4 className="text-sm font-semibold text-gray-800">4. Buscar e importar chamados</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              Confira os chamados antes de criar os cards. Os já importados aparecem marcados e não duplicam.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={buscarTickets}
              disabled={buscandoTickets || !config.url}
              className="inline-flex items-center gap-2 px-3.5 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 text-sm font-semibold rounded-lg"
            >
              {buscandoTickets ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {buscandoTickets ? 'Buscando...' : 'Buscar chamados'}
            </button>
            <button
              type="button"
              onClick={importar}
              disabled={importando || selecionados.size === 0 || !destinoOk || !config.enabled}
              title={
                !config.enabled
                  ? 'Ative a integração e salve a configuração'
                  : !destinoOk
                    ? 'Escolha o fluxo e a etapa de destino'
                    : undefined
              }
              className="inline-flex items-center gap-2 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg"
            >
              {importando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {importando ? 'Importando...' : `Importar ${selecionados.size || ''}`.trim()}
            </button>
          </div>
        </div>

        {importResultado && (
          <div
            className={`rounded-lg border p-3 text-sm ${
              importResultado.status === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <div className="font-medium">{importResultado.message}</div>
            {Array.isArray(importResultado.avisos) && importResultado.avisos.length > 0 && (
              <ul className="text-xs mt-1 list-disc list-inside">
                {importResultado.avisos.map((a: string, i: number) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tickets.length > 0 ? (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={selecionados.size > 0 && selecionados.size === tickets.filter(t => !t.jaImportado).length}
                      onChange={e =>
                        setSelecionados(
                          e.target.checked
                            ? new Set(tickets.filter(t => !t.jaImportado).map(t => t.id))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-medium">#</th>
                  <th className="px-3 py-2 text-left font-medium">Requerente</th>
                  <th className="px-3 py-2 text-left font-medium">Título</th>
                  <th className="px-3 py-2 text-left font-medium">Categoria</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tickets.map(t => (
                  <tr key={t.id} className={t.jaImportado ? 'bg-gray-50 text-gray-400' : 'hover:bg-sky-50/50'}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        disabled={t.jaImportado}
                        checked={selecionados.has(t.id)}
                        onChange={() => toggleSelecionado(t.id)}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{t.id}</td>
                    <td className="px-3 py-2">{t.requerente || <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 max-w-[280px]">
                      <div className="truncate" title={t.titulo}>{t.titulo}</div>
                      {t.descricao && (
                        <div className="text-[11px] text-gray-400 truncate" title={t.descricao}>
                          {t.descricao}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">{t.categoria || <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {t.jaImportado ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">já importado</span>
                      ) : (
                        <span className="text-xs">{t.statusNome}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            Clique em <strong>Buscar chamados</strong> para ver os tickets do GLPI.
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegracaoGlpi;
